import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Search } from 'lucide-react';
import { Button, Checkbox, DateField, FormField, FormGrid, Select, SubTableEditor, TextField } from '@/components';
import { api, errorMessage } from '@/lib/api';
import { useBusinessOperators, useCodeValues, type TaxRounding } from '../api';
import { CellError, NumCell } from './cells';
import { MasterEditor } from './MasterEditor';

type Err = { message?: string } | undefined;
const msg = (e: unknown): string | undefined => (e as Err)?.message;
const nz = (v: string | null | undefined) => v ?? '';

// =============================================================== 事業実施者 年度別設定
interface SettingRow {
  businessOperatorId: number;
  name: string;
  isNew: boolean;
  hasConsumptionTax: boolean;
  invoiceTaxSubsidy: boolean;
  taxRounding: TaxRounding;
  displayOrder: number | null;
  note: string;
}
interface SettingsForm {
  items: SettingRow[];
}
interface SettingsResp {
  items: { businessOperatorId: number; hasConsumptionTax: boolean; invoiceTaxSubsidy: boolean; taxRounding: TaxRounding; displayOrder: number; note: string | null }[];
}

export function OperatorSettingsEditor({ year }: { year: number }) {
  const { data: operators } = useBusinessOperators(undefined, { includeInactive: true });
  const { data: codes } = useCodeValues();
  const nameOf = useMemo(() => new Map((operators ?? []).map((o) => [o.id, o.name])), [operators]);
  const active = useMemo(() => (operators ?? []).filter((o) => o.isActive), [operators]);

  return (
    <MasterEditor<SettingsForm, SettingsResp>
      title="事業実施者 年度別設定"
      description="消費税の有無・インボイス消費税の助成・税の端数処理・表示順を事業実施者ごとに設定します。年度設定が無い事業実施者は既定値で追加されます。"
      path={`/masters/years/${year}/operator-settings`}
      ready={!!operators}
      savedMessage="事業実施者の年度別設定を保存しました"
      toForm={(d) => {
        const have = new Set(d.items.map((i) => i.businessOperatorId));
        const rows: SettingRow[] = d.items.map((i) => ({ ...i, name: nameOf.get(i.businessOperatorId) ?? `ID ${i.businessOperatorId}`, isNew: false, note: nz(i.note), displayOrder: i.displayOrder }));
        const missing = active.filter((o) => !have.has(o.id)).map<SettingRow>((o) => ({ businessOperatorId: o.id, name: o.name, isNew: true, hasConsumptionTax: false, invoiceTaxSubsidy: true, taxRounding: '四捨五入', displayOrder: 0, note: '' }));
        return { items: [...rows, ...missing] };
      }}
      toBody={(v) => ({ items: v.items.map(({ name: _n, isNew: _i, ...r }) => ({ ...r, displayOrder: r.displayOrder ?? 0, note: r.note || null })) })}
    >
      {({ control, register, formState: { errors } }) => (
        <SubTableEditor
          control={control}
          name="items"
          reorderable={false}
          readOnly
          createRow={() => ({ businessOperatorId: 0, name: '', isNew: true, hasConsumptionTax: false, invoiceTaxSubsidy: true, taxRounding: '四捨五入' as const, displayOrder: 0, note: '' })}
          emptyMessage="事業実施者がありません"
          columns={[
            { key: 'name', header: '事業実施者', width: 190, render: ({ field }) => <span><strong>{field.name}</strong>{field.isNew && <span className="bs-plan-error" style={{ color: 'var(--warning-600)' }}> （未設定・新規追加）</span>}</span> },
            { key: 'tax', header: '消費税', width: 110, align: 'center', render: ({ path }) => <Controller control={control} name={`${path}.hasConsumptionTax`} render={({ field }) => <Checkbox aria-label="消費税あり" label="あり" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} /> },
            { key: 'invoice', header: 'インボイス消費税_助成', width: 150, align: 'center', render: ({ path }) => <Controller control={control} name={`${path}.invoiceTaxSubsidy`} render={({ field }) => <Checkbox aria-label="インボイス消費税の助成あり" label="あり" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />} /> },
            { key: 'rounding', header: '税算出', width: 130, render: ({ path }) => <Select aria-label="税算出" options={(codes?.taxRoundings ?? []).map((v) => ({ value: v, label: v }))} {...register(`${path}.taxRounding`)} /> },
            { key: 'order', header: '表示順', width: 100, align: 'right', render: ({ path, index }) => <NumCell control={control} name={`${path}.displayOrder`} label="表示順" width="full" error={msg(errors.items?.[index]?.displayOrder)} /> },
            { key: 'note', header: '備考', render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
          ]}
        />
      )}
    </MasterEditor>
  );
}

// =============================================================== 用船料
interface FeeForm {
  items: { tonnageFrom: number | null; tonnageTo: number | null; fee: number | null; note: string }[];
}
export function CharterFeesEditor({ year }: { year: number }) {
  return (
    <MasterEditor<FeeForm, { items: { tonnageFrom: number; tonnageTo: number; fee: number; note: string | null }[] }>
      title="用船料マスタ"
      description="船舶トン数が「トン数FROM 以上 トン数TO 以下」の行の用船料（円/日・税抜）を適用します。範囲が重なる行は保存できません。"
      path={`/masters/years/${year}/charter-fees`}
      savedMessage="用船料を保存しました"
      toForm={(d) => ({ items: d.items.map((i) => ({ ...i, note: nz(i.note) })) })}
      toBody={(v) => ({ items: v.items.map((i) => ({ tonnageFrom: i.tonnageFrom ?? 0, tonnageTo: i.tonnageTo ?? 0, fee: i.fee ?? 0, note: i.note || null })) })}
    >
      {({ control, register, formState: { errors } }) => (
        <SubTableEditor
          control={control}
          name="items"
          title="用船料"
          createRow={() => ({ tonnageFrom: null, tonnageTo: null, fee: null, note: '' })}
          columns={[
            { key: 'from', header: 'トン数 FROM', width: 140, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.tonnageFrom`} label="トン数FROM" decimals={2} unit="t" error={msg(errors.items?.[index]?.tonnageFrom)} /> },
            { key: 'to', header: 'トン数 TO', width: 140, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.tonnageTo`} label="トン数TO" decimals={2} unit="t" error={msg(errors.items?.[index]?.tonnageTo)} /> },
            { key: 'fee', header: '用船料（円/日・税抜）', width: 180, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.fee`} label="用船料" unit="円" error={msg(errors.items?.[index]?.fee)} /> },
            { key: 'note', header: '備考', render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
          ]}
        />
      )}
    </MasterEditor>
  );
}

// =============================================================== 賃金
interface WageForm {
  items: { hoursFrom: number | null; hoursTo: number | null; wage: number | null; dailyAllowance: number | null; insurance: number | null; mealCost: number | null; note: string }[];
}
export function WagesEditor({ year }: { year: number }) {
  return (
    <MasterEditor<WageForm, { items: { hoursFrom: number; hoursTo: number; wage: number; dailyAllowance: number; insurance: number; mealCost: number; note: string | null }[] }>
      title="賃金マスタ"
      description="調査時間（8時間で頭打ち）が「作業時間FROM 以上 作業時間TO 以下」の行の賃金（円/人・保険料込み）を適用します。内訳は帳票用です。"
      path={`/masters/years/${year}/wages`}
      savedMessage="賃金を保存しました"
      toForm={(d) => ({ items: d.items.map((i) => ({ ...i, note: nz(i.note) })) })}
      toBody={(v) => ({
        items: v.items.map((i) => ({ hoursFrom: i.hoursFrom ?? 0, hoursTo: i.hoursTo ?? 0, wage: i.wage ?? 0, dailyAllowance: i.dailyAllowance ?? 0, insurance: i.insurance ?? 0, mealCost: i.mealCost ?? 0, note: i.note || null })),
      })}
    >
      {({ control, register, formState: { errors } }) => (
        <SubTableEditor
          control={control}
          name="items"
          title="賃金"
          createRow={() => ({ hoursFrom: null, hoursTo: null, wage: null, dailyAllowance: 0, insurance: 0, mealCost: 0, note: '' })}
          columns={[
            { key: 'from', header: '作業時間 FROM', width: 120, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.hoursFrom`} label="作業時間FROM" decimals={1} unit="h" error={msg(errors.items?.[index]?.hoursFrom)} /> },
            { key: 'to', header: '作業時間 TO', width: 120, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.hoursTo`} label="作業時間TO" decimals={1} unit="h" error={msg(errors.items?.[index]?.hoursTo)} /> },
            { key: 'wage', header: '賃金（円/人）', width: 140, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.wage`} label="賃金" unit="円" error={msg(errors.items?.[index]?.wage)} /> },
            { key: 'allowance', header: '日当', width: 120, align: 'right', render: ({ path }) => <NumCell control={control} name={`${path}.dailyAllowance`} label="日当" /> },
            { key: 'insurance', header: '保険', width: 110, align: 'right', render: ({ path }) => <NumCell control={control} name={`${path}.insurance`} label="保険" /> },
            { key: 'meal', header: '食費', width: 110, align: 'right', render: ({ path }) => <NumCell control={control} name={`${path}.mealCost`} label="食費" /> },
            { key: 'note', header: '備考', render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
          ]}
        />
      )}
    </MasterEditor>
  );
}

// =============================================================== 農林漁区番号表
interface ZoneForm {
  items: { northLat: number | null; eastLon: number | null; zoneNo: number | null; seaAreaClass: string; note: string }[];
}
export function FisheryZonesEditor({ year }: { year: number }) {
  const { data: codes } = useCodeValues();
  return (
    <>
      <MasterEditor<ZoneForm, { items: { northLat: number; eastLon: number; zoneNo: number; seaAreaClass: string | null; note: string | null }[] }>
        title="農林漁区番号表"
        description="北緯・東経は「度×10＋分の十の位」形式（例：北緯21°30′→213、東経122°00′→1220）で区画の南西端を表します。海域区分は S・A・B（該当なしは空欄）。"
        path={`/masters/years/${year}/fishery-zones`}
        savedMessage="農林漁区番号表を保存しました"
        toForm={(d) => ({ items: d.items.map((i) => ({ ...i, seaAreaClass: i.seaAreaClass ?? '', note: nz(i.note) })) })}
        toBody={(v) => ({ items: v.items.map((i) => ({ northLat: i.northLat ?? 0, eastLon: i.eastLon ?? 0, zoneNo: i.zoneNo ?? 0, seaAreaClass: i.seaAreaClass || null, note: i.note || null })) })}
      >
        {({ control, register, formState: { errors } }) => (
          <SubTableEditor
            control={control}
            name="items"
            title="農林漁区番号"
            reorderable={false}
            createRow={() => ({ northLat: null, eastLon: null, zoneNo: null, seaAreaClass: '', note: '' })}
            columns={[
              { key: 'lat', header: '北緯', width: 110, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.northLat`} label="北緯" error={msg(errors.items?.[index]?.northLat)} /> },
              { key: 'lon', header: '東経', width: 110, align: 'right', required: true, render: ({ path }) => <NumCell control={control} name={`${path}.eastLon`} label="東経" /> },
              { key: 'zone', header: '農林漁区番号', width: 120, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.zoneNo`} label="農林漁区番号" error={msg(errors.items?.[index]?.zoneNo)} /> },
              { key: 'class', header: '海域区分', width: 110, render: ({ path }) => <Select aria-label="海域区分" placeholder="なし" options={(codes?.seaAreaClasses ?? []).map((v) => ({ value: v, label: v }))} {...register(`${path}.seaAreaClass`)} /> },
              { key: 'note', header: '備考', render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
            ]}
          />
        )}
      </MasterEditor>
      <ZoneResolveTester year={year} />
    </>
  );
}

/** 緯度経度から農林漁区番号・海域区分を算出する動作確認（保存済みのマスタで算出） */
function ZoneResolveTester({ year }: { year: number }) {
  const [v, setV] = useState({ latDeg: '', latMin: '', latSec: '', lonDeg: '', lonMin: '', lonSec: '' });
  const [result, setResult] = useState<string>();
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((s) => ({ ...s, [k]: e.target.value }));
  const run = async () => {
    try {
      const r = await api.get<{ zoneNo: number | null; seaAreaClass: string | null }>(`/masters/years/${year}/fishery-zones/resolve`, v);
      setResult(r.zoneNo == null ? '農林漁区番号を算出できませんでした（入力値またはマスタを確認してください）' : `農林漁区番号 ${r.zoneNo}　海域区分 ${r.seaAreaClass ?? 'なし'}`);
    } catch (e) {
      setResult(errorMessage(e));
    }
  };
  return (
    <section className="bs-panel" style={{ marginTop: 16 }}>
      <header className="bs-panel__header"><div><h2 className="bs-panel__title">算出テスト</h2><p className="bs-panel__desc">保存済みの農林漁区番号表で、緯度経度（度・分・秒）から農林漁区番号と海域区分を確認できます。</p></div></header>
      <div className="bs-panel__body">
        <FormGrid columns={4}>
          <FormField label="北緯 度"><TextField inputMode="numeric" value={v.latDeg} onChange={set('latDeg')} /></FormField>
          <FormField label="北緯 分"><TextField inputMode="numeric" value={v.latMin} onChange={set('latMin')} /></FormField>
          <FormField label="東経 度"><TextField inputMode="numeric" value={v.lonDeg} onChange={set('lonDeg')} /></FormField>
          <FormField label="東経 分"><TextField inputMode="numeric" value={v.lonMin} onChange={set('lonMin')} /></FormField>
        </FormGrid>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
          <Button icon={<Search size={16} />} onClick={run}>算出する</Button>
          {result && <strong role="status">{result}</strong>}
        </div>
      </div>
    </section>
  );
}

// =============================================================== 年間限度調査日数
interface LimitForm {
  sDays: number | null;
  aDays: number | null;
  bDays: number | null;
  totalDays: number | null;
  note: string;
  extras: { seaAreaClass: string; dateFrom: string; dateTo: string; plusDays: number | null; note: string }[];
}
interface LimitResp {
  limit: { sDays: number; aDays: number; bDays: number; totalDays: number; note: string | null } | null;
  extras: { seaAreaClass: string; dateFrom: string; dateTo: string; plusDays: number; note: string | null }[];
}
export function SurveyDayLimitsEditor({ year }: { year: number }) {
  const { data: codes } = useCodeValues();
  return (
    <MasterEditor<LimitForm, LimitResp>
      title="年間限度調査日数"
      description="1隻あたりの年間の調査日数の上限（S・A・B 海域別と合計）です。プラス日数は、対象期間中の S 海域調査に上限を加算する特例です。"
      path={`/masters/years/${year}/survey-day-limits`}
      savedMessage="年間限度調査日数を保存しました"
      mapErrorPath={(p) => p.replace(/^limit\./, '')}
      toForm={(d) => ({
        sDays: d.limit?.sDays ?? 0,
        aDays: d.limit?.aDays ?? 0,
        bDays: d.limit?.bDays ?? 0,
        totalDays: d.limit?.totalDays ?? 0,
        note: nz(d.limit?.note),
        extras: d.extras.map((e) => ({ ...e, note: nz(e.note) })),
      })}
      toBody={(v) => ({
        limit: { sDays: v.sDays ?? 0, aDays: v.aDays ?? 0, bDays: v.bDays ?? 0, totalDays: v.totalDays ?? 0, note: v.note || null },
        extras: v.extras.map((e) => ({ seaAreaClass: e.seaAreaClass || 'S', dateFrom: e.dateFrom, dateTo: e.dateTo, plusDays: e.plusDays ?? 0, note: e.note || null })),
      })}
    >
      {({ control, register, formState: { errors } }) => (
        <div style={{ display: 'grid', gap: 20 }}>
          <FormGrid columns={4}>
            <FormField label="S 海域 上限日数" required error={msg(errors.sDays)}><NumCell control={control} name="sDays" label="S海域上限日数" unit="日" /></FormField>
            <FormField label="A 海域 上限日数" required error={msg(errors.aDays)}><NumCell control={control} name="aDays" label="A海域上限日数" unit="日" /></FormField>
            <FormField label="B 海域 上限日数" required error={msg(errors.bDays)}><NumCell control={control} name="bDays" label="B海域上限日数" unit="日" /></FormField>
            <FormField label="合計 上限日数" required error={msg(errors.totalDays)} hint="計画日数・速報の限度チェックに使用"><NumCell control={control} name="totalDays" label="合計上限日数" unit="日" /></FormField>
            <FormField label="備考" span={4}><TextField {...register('note')} /></FormField>
          </FormGrid>
          <SubTableEditor
            control={control}
            name="extras"
            title="年間限度調査プラス日数"
            createRow={() => ({ seaAreaClass: 'S', dateFrom: '', dateTo: '', plusDays: null, note: '' })}
            emptyMessage="プラス日数はありません"
            columns={[
              { key: 'class', header: '対象海域', width: 100, render: ({ path }) => <Select aria-label="対象海域" options={(codes?.seaAreaClasses ?? ['S']).map((v) => ({ value: v, label: v }))} {...register(`${path}.seaAreaClass`)} /> },
              { key: 'from', header: '対象年月日 FROM', width: 170, required: true, render: ({ path, index }) => <><DateField aria-label="対象年月日FROM" invalid={!!errors.extras?.[index]?.dateFrom} {...register(`${path}.dateFrom`)} /><CellError message={msg(errors.extras?.[index]?.dateFrom)} /></> },
              { key: 'to', header: '対象年月日 TO', width: 170, required: true, render: ({ path, index }) => <><DateField aria-label="対象年月日TO" invalid={!!errors.extras?.[index]?.dateTo} {...register(`${path}.dateTo`)} /><CellError message={msg(errors.extras?.[index]?.dateTo)} /></> },
              { key: 'plus', header: '上限プラス日数', width: 140, align: 'right', required: true, render: ({ path, index }) => <NumCell control={control} name={`${path}.plusDays`} label="上限プラス日数" unit="日" error={msg(errors.extras?.[index]?.plusDays)} /> },
              { key: 'note', header: '備考', render: ({ path }) => <TextField aria-label="備考" {...register(`${path}.note`)} /> },
            ]}
          />
        </div>
      )}
    </MasterEditor>
  );
}

// =============================================================== 港名
interface PortForm {
  items: { businessOperatorId: number | null; name: string; displayOrder: number | null }[];
}
export function PortsEditor({ year }: { year: number }) {
  const { data: operators } = useBusinessOperators(undefined, { includeInactive: true });
  return (
    <MasterEditor<PortForm, { items: { businessOperatorId: number; name: string; displayOrder: number }[] }>
      title="港名マスタ"
      description="事業実施者ごとの港名です。事前連絡の「港名」の選択肢に使われます。"
      path={`/masters/years/${year}/ports`}
      ready={!!operators}
      savedMessage="港名を保存しました"
      toForm={(d) => ({ items: d.items.map((i) => ({ businessOperatorId: i.businessOperatorId, name: i.name, displayOrder: i.displayOrder })) })}
      toBody={(v) => ({ items: v.items.map((i) => ({ businessOperatorId: i.businessOperatorId ?? 0, name: i.name, displayOrder: i.displayOrder ?? 0 })) })}
    >
      {({ control, register, formState: { errors } }) => (
        <SubTableEditor
          control={control}
          name="items"
          title="港名"
          createRow={() => ({ businessOperatorId: null, name: '', displayOrder: 0 })}
          columns={[
            {
              key: 'op',
              header: '事業実施者',
              width: 220,
              required: true,
              render: ({ path, index }) => (
                <Controller
                  control={control}
                  name={`${path}.businessOperatorId`}
                  render={({ field }) => (
                    <>
                      <Select aria-label="事業実施者" placeholder="選択してください" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))} invalid={!!errors.items?.[index]?.businessOperatorId} options={(operators ?? []).map((o) => ({ value: o.id, label: o.name }))} />
                      <CellError message={msg(errors.items?.[index]?.businessOperatorId)} />
                    </>
                  )}
                />
              ),
            },
            { key: 'name', header: '港名', required: true, render: ({ path, index }) => <><TextField aria-label="港名" maxLength={50} invalid={!!errors.items?.[index]?.name} {...register(`${path}.name`)} /><CellError message={msg(errors.items?.[index]?.name)} /></> },
            { key: 'order', header: '表示順', width: 100, align: 'right', render: ({ path }) => <NumCell control={control} name={`${path}.displayOrder`} label="表示順" /> },
          ]}
        />
      )}
    </MasterEditor>
  );
}
